import { BooleanInput } from '@angular/cdk/coercion';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
} from '@angular/core';
import { Router } from '@angular/router';
import { AuthService, HemroRoles, ProfileService, User, UserService } from '@hemro/lib/profile';
import { LayoutComponent } from 'app/layout/layout.component';
import { Subject, takeUntil } from 'rxjs';
import { Nullable } from 'simplytyped';


@Component({
    selector       : 'user',
    templateUrl    : './user.component.html',
    encapsulation  : ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs       : 'user',
})
export class UserComponent implements OnInit, OnDestroy
{
    /* eslint-disable @typescript-eslint/naming-convention */
    static ngAcceptInputType_showAvatar: BooleanInput;
    /* eslint-enable @typescript-eslint/naming-convention */

    @Input() showAvatar: boolean          = true;
    user: User;
    userEmail: string                     = '';
    companyName: Nullable<string>;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    /**
     * Constructor
     */
    constructor(
        private _changeDetectorRef: ChangeDetectorRef,
        private _router: Router,
        private _userService: UserService,
        private _profileService: ProfileService,
        private _auth: AuthService,
        private _layoutComponent: LayoutComponent,
    )
    {
        this.userEmail = _profileService.profile
            ? _profileService.profile.user.username
            : (_userService.user ? _userService.user.username : '');

        this.companyName = _profileService.profile.company?.info.name || null;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------
    get profileId(): string
    {
        return this._profileService.profile.profileId;
    }

    get username(): string {
        return this._profileService.profile
            ? this._profileService.profile.displayName
            : (this._userService.user ? this._userService.user.username : '');
    }

    get canEditCompanyWifi() {
        return !!this._profileService.profile?.company && this._userService.hasRole(HemroRoles.editMyCompany)
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void
    {
        // Subscribe to user changes
        this._userService.user$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((user: User) =>
            {
                this.user = user;

                // Mark for check
                this._changeDetectorRef.markForCheck();
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void
    {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Update the user status
     *
     * @param status
     */
    updateUserStatus(status: string): void
    {
        // Return if user is not available
        if (!this.user) {
            return;
        }

        // Update the user
        this._userService.update({
            ...this.user,
        }).subscribe();
    }

    toggleSettings(): void
    {
        this._layoutComponent.toggleSettings();
    }

    updateCompanyWifi(): void {
        this._router.navigate([
            'company',
            `${this._profileService.profile?.company.companyId}`,
            'wifi'
        ]);
    }

    /**
     * Sign out
     */
    signOut(): void
    {
        this._router.navigate(['/sign-out']);
    }
}
